/* eslint-disable security/detect-object-injection */
/* eslint-disable react/no-danger */
/* eslint-disable @typescript-eslint/no-explicit-any */

// libraries
import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react';
import {useQuery, useMutation} from 'react-query';
import {Link} from 'react-router-dom';
import _ from 'lodash';
import cx from 'classnames';
import {
  ErrorBoundary,
  SearchProvider,
  ResultsPerPage,
  Paging,
  Sorting,
  WithSearch,
  withSearch,
  Facet,
} from '@elastic/react-search-ui';
import '@elastic/react-search-ui-views/lib/styles/styles.css';
import {HiOutlineX} from 'react-icons/hi';
import {
  RequestState,
  SearchRequest,
} from '@segmed/search-ui-elasticsearch-connector';
import {SearchContextState, SearchQuery} from '@elastic/search-ui';
import {toast} from 'react-toastify';

// utils and functions
import {useAuth} from '../../../hooks/auth';
import {
  ReportSelectionEventType,
  isAnyReportSelected,
  isReportSelected,
  useReportSelection,
} from '../../../hooks/report-selection-provider';
import {
  buildSortOptionsFromConfig,
  getFacetFields,
} from '../../../config/config-helper';
import {trackEvent} from '../../../utils/tracking';
import {getAvailableTags} from '../../../models/tags';
import {formatNumber} from '../../../utils/strings';
import {fetchReport, truncateStudyID} from '../../../models/report';
import {
  fetchDicomStudyInfo,
  getDicomSeries,
  getDicomAvailability,
} from '../../../models/dicom-viewer';
import {
  parseElasticSearchBody,
  extractSearchTerms,
  highlightSearchterm,
  fetchMatchingStudyIds,
  PatientSortOption,
  ExtendedSearchDriverOptions,
  intersperseAdditionalStudies,
  buildElasticQuery,
  SearchBox,
  newSearchBox,
} from '../../../models/minerva';
import {useAxios} from 'src/utils/http';
import {isInternal} from '../../../models/auth';

// components
import {Toggle} from '../../../core/components/toggle';
import {MinervaSearchForm} from '../../../components/minerva-search-form/MinervaSearchForm';
import AgeRangeFilter from '../../../components/minerva-search-results/AgeRangeFilter';
import {
  PatientSearch,
  SelectCustomNumberReportsModal,
} from '../../../components/minerva-search-results';
import {ToastMessage} from '../../../core/components/toast';
import {SearchTableRows} from '../../../components/minerva-search-results/SearchTableRows';
import {MenuDropdownSingle} from '../../../core/components/menu';
import {ReportComponent} from '../../../components/report';
import {Loading} from '../../../core/components/loading';
import {TagDropdown} from '../../../components/tags';
// import {AddToDatasetDropdown} from '../../../components/dataset';
import {SaveSearchButton} from '../../../components/save-search';
import {MultiCheckboxFacet} from '@elastic/react-search-ui-views';
import {AddToProjectDatasetDropdown} from 'src/components/dataset/add-to-project-dataset-dropdown';

export const formatFieldLabel = (field: string): string => {
  let res = field;
  if (res.endsWith('.keyword')) {
    res = res.substring(0, res.length - '.keyword'.length);
  }
  res = res.replace(/_/g, ' ');
  return res;
};

const ClearFilters = ({filters, clearFilters}: any) => {
  return (
    <button
      data-cy="clearFilterButton"
      onClick={() => clearFilters()}
      disabled={filters.length === 0}
      className="btn btn-secondary my-4"
    >
      Clear {filters.length} Filters
    </button>
  );
};

const ClearFilterButton = withSearch(({filters, clearFilters}) => ({
  filters,
  clearFilters,
}))(ClearFilters);

const ScrollRestorer = React.memo(
  ({
    tableContainerRef,
    facetContainerRef,
    scrollPosRef,
    dependencies,
  }: {
    tableContainerRef: React.RefObject<HTMLDivElement>;
    facetContainerRef: React.RefObject<HTMLDivElement>;
    scrollPosRef: React.MutableRefObject<{
      scrollTop: number;
      scrollLeft?: number;
    }>;
    dependencies: any[];
  }) => {
    useLayoutEffect(() => {
      if (tableContainerRef.current && facetContainerRef.current) {
        tableContainerRef.current.scrollTo({
          top: scrollPosRef.current.scrollTop,
          left: scrollPosRef.current.scrollLeft,
        });
        facetContainerRef.current.scrollTo({
          top: scrollPosRef.current.scrollTop,
        });
      }
    }, dependencies);

    return null;
  }
);

export const MinervaSearch = ({
  config,
  lastRequestBody,
  splitView = false,
  toggleGroupPatientIDs = () => {},
  onSubmit,
  patientCount,
  setSortPatientIDsBy = () => {},
  isInternalPage = false,
  toggleVectorSearch = () => {},
  projectId,
}: {
  config: ExtendedSearchDriverOptions;
  lastRequestBody?: SearchRequest;
  splitView?: boolean;
  toggleGroupPatientIDs?: () => void;
  onSubmit: (query?: Object) => void;
  patientCount?: number | null;
  setSortPatientIDsBy?: (option: PatientSortOption) => void;
  isInternalPage?: boolean;
  toggleVectorSearch?: () => void;
  projectId?: any;
}) => {
  const api = useAxios();

  const [openedStudyID, openedStudyIDChange] = useState<string>();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const facetContainerRef = useRef<HTMLDivElement>(null);
  const {reportSelectionState, reportSelectionDispatch} = useReportSelection();
  const [showAllStudiesPerPatient, showAllStudiesPerPatientChange] =
    useState<boolean>(false);
  const [additionalPatientStudies, additionalPatientStudiesChange] = useState<
    any[]
  >([]);
  const [studyIDs, studyIDsChange] = useState<string[]>([]);
  const [searchQueryWithFilter, searchQueryWithFilterChange] =
    useState<SearchQuery>();

  const {authState} = useAuth();
  const internalUser = isInternal(authState.profile!.email);

  const [customReportSelectionModalOpen, customReportSelectionModalOpenChange] =
    useState<boolean>(false);

  const studiesSelected = isAnyReportSelected(reportSelectionState);

  const {data: tags, isLoading: tagsLoading} = useQuery(
    ['tags'],
    () => getAvailableTags(api),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
  const userTags = useMemo(() => {
    if (tags) {
      return tags.filter(tag => tag.scope === 'user');
    }
    return [];
  }, [tags]);

  const {
    data: report,
    error: reportError,
    isLoading: reportLoading,
  } = useQuery(
    ['report', openedStudyID],
    () => {
      return fetchReport(api, openedStudyID as string);
    },
    {
      enabled: !_.isEmpty(openedStudyID),
      keepPreviousData: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const {data: openedDicomStudy} = useQuery(
    ['openedDicomStudy', openedStudyID],
    () => {
      return fetchDicomStudyInfo(api, openedStudyID!);
    },
    {
      enabled: !_.isNil(openedStudyID),
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const fetchStudyIdsMutation = useMutation(
    (params: {
      config: ExtendedSearchDriverOptions;
      requestState: RequestState;
      queryConfig: Object;
      numStudies?: number;
    }) => fetchMatchingStudyIds(params),
    {
      onMutate: async () => {
        // Unselect all reports before the mutation starts
        reportSelectionDispatch({
          type: ReportSelectionEventType.UNSELECT_ALL,
        });
      },
      onSuccess: studyIDs => {
        reportSelectionDispatch({
          type: ReportSelectionEventType.SELECT,
          payload: studyIDs,
        });
      },
    }
  );

  const {data: dicomAvailability = []} = useQuery(
    ['dicomAvailabilities', studyIDs],
    () => {
      return getDicomAvailability(api, studyIDs!);
    },
    {
      enabled: !_.isEmpty(studyIDs),
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
      onError: () => {
        const errMessage = 'Something went wrong fetching DICOM availability';
        toast(<ToastMessage title={errMessage} icon="error" />);
      },
    }
  );

  useEffect(() => {
    // update default text fields with lastRequestBody on load if applicable
    if (lastRequestBody) {
      const res = parseElasticSearchBody(lastRequestBody);
      const searchBoxes = res.searchBoxes;

      if (searchBoxes.length === 0) {
        searchBoxesChange([newSearchBox()]);
      } else {
        searchBoxesChange(searchBoxes);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchBoxes, searchBoxesChange] = useState<SearchBox[]>([
    newSearchBox(),
  ]);

  const CustomErrorView = ({error, children}: {error: any; children: any}) => {
    if (!error) {
      return children;
    }
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <p className="text-center text-xl mb-4">
          Whoops! Searching through tens of millions of reports is no small
          feat! Our servers are catching their breath. Click refresh to jump
          back into your search for data.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="btn btn-primary"
        >
          Refresh
        </button>
      </div>
    );
  };
  const urlObject = new URL(window.location.href);
  // check if exists the autosubmit get param and if it's  "true"
  const autoSubmit = urlObject.searchParams.get('autosubmit') === 'true';

  const scrollPosRef = useRef<{scrollTop: number; scrollLeft?: number}>({
    scrollTop: 0,
    scrollLeft: 0,
  });

  // Save current scroll position
  const saveScrollPos = () => {
    if (tableContainerRef.current) {
      scrollPosRef.current = {
        scrollTop: tableContainerRef.current.scrollTop,
        scrollLeft: tableContainerRef.current.scrollLeft,
      };
    }
  };
  const saveScrollPosForFacet = () => {
    if (facetContainerRef.current) {
      scrollPosRef.current = {
        scrollTop: facetContainerRef.current.scrollTop,
      };
    }
  };

  const handleFacetSelect = () => {
    saveScrollPosForFacet();
  };

  const isSearchNotEmpty = (lastRequestBody: any) => {
    const searchBody = parseElasticSearchBody(lastRequestBody);
    return searchBody.searchBoxes.length > 0 || searchBody.filters.length > 0;
  };

  const isSaveSearchDisabled =
    !lastRequestBody || !isSearchNotEmpty(lastRequestBody);

  return (
    <SearchProvider config={config}>
      <div>
        <div className="flex flex-col gap-y-4 p-4">
          <div className="">
            <MinervaSearchForm
              data-cy="SearchFormInput"
              onSubmit={query => {
                onSubmit(query);
              }}
              queryState={searchBoxes}
              queryStateChange={query => {
                searchBoxesChange(query);
              }}
              autoSubmit={autoSubmit}
            />
          </div>

          <WithSearch
            mapContextToProps={({
              wasSearched,
              results,
              rawResponse,
              isLoading,
              totalResults,
              pagingStart,
              pagingEnd,
              current,
              filters,
              resultsPerPage,
              searchTerm,
              sortDirection,
              sortField,
              sortList,
              sort,
              conditionalFacets,
              disjunctiveFacets,
              disjunctiveFacetsAnalyticsTags,
              search_fields,
            }) => ({
              wasSearched,
              results,
              rawResponse,
              isLoading,
              totalResults,
              pagingStart,
              pagingEnd,
              current,
              filters,
              resultsPerPage,
              searchTerm,
              sortDirection,
              sortField,
              sortList,
              sort,
              conditionalFacets,
              disjunctiveFacets,
              disjunctiveFacetsAnalyticsTags,
              search_fields,
            })}
          >
            {({
              wasSearched,
              results,
              rawResponse,
              isLoading,
              totalResults,
              pagingStart,
              pagingEnd,
              current,
              filters,
              resultsPerPage,
              searchTerm,
              sortDirection,
              sortField,
              sortList,
            }) => {
              const newStudyIDs = _.map(
                results,
                result => result['study_id']?.['raw']
              );
              const studyCount = _.find(
                _.find(rawResponse.facets, {
                  identifier: 'orig_lang.keyword',
                })?.entries,
                {label: 'en'}
              )?.count;

              // Update studyIDs only if they are different from the current state
              if (!_.isEqual(newStudyIDs, studyIDs)) {
                studyIDsChange(newStudyIDs);
              }

              const newSearchQueryWithFilter = {
                filters,
                searchQuery: config.searchQuery,
              };

              if (!_.isEqual(newSearchQueryWithFilter, searchQueryWithFilter)) {
                reportSelectionDispatch({
                  type: ReportSelectionEventType.UNSELECT_ALL,
                });
                searchQueryWithFilterChange(newSearchQueryWithFilter);
              }

              const searchTermSet = extractSearchTerms(lastRequestBody!);
              // get the first element of searchTermSet
              searchTerm =
                searchTermSet.size > 0
                  ? searchTermSet.values().next().value
                  : '';

              const AISortingDisabled =
                isLoading ||
                !lastRequestBody ||
                searchTermSet.size === 0 ||
                config.searchQuery.groupPatientIDs ||
                searchBoxes.some(
                  (box: SearchBox) => box.operation !== 'contains substring'
                );

              const requestState = {
                current,
                filters,
                resultsPerPage,
                searchTerm,
                sortDirection,
                sortField,
                sortList,
              };

              const queryConfig = {
                disjunctiveFacets: config.searchQuery.disjunctiveFacets,
                facets: config.searchQuery.facets,
                result_fields: config.searchQuery.result_fields,
                search_fields: config.searchQuery.search_fields,
                groupPatientIDs: config.searchQuery.groupPatientIDs,
              };

              const pageAllSelected =
                results.length > 0 &&
                _.every(results, result =>
                  isReportSelected(
                    reportSelectionState,
                    result['study_id']['raw']
                  )
                );

              const pageSomeSelected =
                pageAllSelected ||
                _.some(results, result =>
                  isReportSelected(
                    reportSelectionState,
                    result['study_id']['raw']
                  )
                );
              const selectPageOfReports = () => {
                trackEvent('SEARCH_SELECT_PAGE');
                saveScrollPos();
                reportSelectionDispatch({
                  type: ReportSelectionEventType.UNSELECT_ALL,
                });
                reportSelectionDispatch({
                  type: ReportSelectionEventType.SELECT,
                  payload: _.map(results, result => result['study_id']['raw']),
                });
              };
              const selectReports = (numStudies: number) => {
                if (numStudies > studyCount) {
                  toast(
                    <ToastMessage
                      title="Number of studies selected exceeds the total number of studies available"
                      icon="error"
                    />
                  );
                  return;
                }
                reportSelectionDispatch({
                  type: ReportSelectionEventType.UNSELECT_ALL,
                });
                const query = buildElasticQuery(searchBoxes);

                fetchStudyIdsMutation.mutate({
                  config: config,
                  // marking searchTerm as empty because it will be constructed from the queryBuilderQuery
                  requestState: {...requestState, searchTerm: undefined},
                  queryConfig: {...queryConfig, queryBuilderQuery: query},
                  numStudies: numStudies,
                });
              };

              const SelectAllReports = () => {
                const totalToSelect = totalResults > 5000 ? 5000 : totalResults;
                trackEvent('SEARCH_SELECT_', {numStudies: totalToSelect});
                return (
                  <div onClick={() => selectReports(totalToSelect)}>
                    {totalResults > 5000
                      ? 'Select top 5000 matches'
                      : `Select all ${totalToSelect} results`}
                  </div>
                );
              };

              const SelectCustomNumberReportsModalWithSearchProps = withSearch(
                () => ({
                  selectReports: (numStudies: number) => {
                    trackEvent('SEARCH_SELECT_CUSTOM', {
                      numStudies: numStudies,
                    });
                    selectReports(numStudies);
                  },
                  closeModal: () => customReportSelectionModalOpenChange(false),
                  isOpen: customReportSelectionModalOpen,
                })
              )(SelectCustomNumberReportsModal);

              const SelectAllReportsButton = withSearch(
                ({filters, facets}: SearchContextState) => ({
                  filters,
                  facets,
                })
              )(SelectAllReports);

              return (
                <ErrorBoundary
                  view={props => {
                    const {error, children} = props;
                    return (
                      <CustomErrorView error={error} children={children} />
                    );
                  }}
                >
                  {customReportSelectionModalOpen && (
                    <SelectCustomNumberReportsModalWithSearchProps />
                  )}
                  {isLoading && (
                    <div className="fixed inset-0 flex items-center justify-center z-50">
                      <div className="inline-flex bg-black rounded p-4">
                        <Loading className="text-white" />
                      </div>
                    </div>
                  )}

                  {wasSearched && (
                    <>
                      <div className="flex justify-between">
                        <div className="flex">
                          <div data-cy="studiesShown">
                            {results.length > 0 &&
                              `Showing ${pagingStart} - ${pagingEnd} out of ${formatNumber(
                                showAllStudiesPerPatient
                                  ? studyCount + additionalPatientStudies.length
                                  : studyCount
                              )} studies available
                            `}
                            {results.length === 0 &&
                              'No search results found for your query'}
                          </div>
                          {config.searchQuery.groupPatientIDs &&
                            patientCount && (
                              <div className="ml-2">
                                {`with ${formatNumber(
                                  patientCount
                                )} Unique Patients`}
                              </div>
                            )}
                        </div>

                        <ResultsPerPage
                          className="mb-4"
                          options={[50, 100]}
                          data-cy="resultsPerPageSelector"
                        />
                      </div>
                      <div className={cx({splitview: splitView})}>
                        <div
                          className={cx({
                            'splitview-panel-l': splitView,
                          })}
                        >
                          <div className="flex gap-x-4 h-[95vh]">
                            <div className="flex flex-col">
                              {!config.searchQuery.useVectorSearch ? (
                                <Sorting
                                  label={'Sort by'}
                                  sortOptions={buildSortOptionsFromConfig()}
                                  className="w-72"
                                  data-cy="sortByDropdown"
                                />
                              ) : (
                                <div className="w-72"></div>
                              )}
                              <ClearFilterButton data-cy="clearFilterButton" />
                              <div
                                ref={facetContainerRef}
                                className="overflow-y-auto h-[80vh]"
                                onScroll={saveScrollPosForFacet}
                              >
                                {getFacetFields().map((field: any) => {
                                  const label = formatFieldLabel(field);
                                  if (
                                    !isInternalPage &&
                                    [
                                      'body_part_llm.keyword',
                                      'contrast.keyword',
                                      'modality_llm.keyword',
                                      'study_description_llm.keyword',
                                    ].includes(field)
                                  ) {
                                    return null;
                                  }
                                  return field === 'patient_id.keyword' ? (
                                    <PatientSearch
                                      key={field}
                                      config={config}
                                      results={results}
                                      lastRequestBody={lastRequestBody}
                                      showAllStudiesPerPatient={
                                        showAllStudiesPerPatient
                                      }
                                      showAllStudiesPerPatientChange={
                                        showAllStudiesPerPatientChange
                                      }
                                      additionalPatientStudiesChange={
                                        additionalPatientStudiesChange
                                      }
                                      toggleGroupPatientIDs={
                                        toggleGroupPatientIDs
                                      }
                                      setSortPatientIDsBy={setSortPatientIDsBy}
                                    />
                                  ) : (
                                    <Facet
                                      key={field}
                                      field={field}
                                      label={label}
                                      filterType="any"
                                      isFilterable
                                      view={props => (
                                        <MultiCheckboxFacet
                                          {...props}
                                          onChange={handleFacetSelect}
                                        />
                                      )}
                                    />
                                  );
                                })}
                                <AgeRangeFilter />
                              </div>
                            </div>

                            <div className="overflow-hidden">
                              <div className="mb-8 flex items-center gap-x-2">
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    data-tour="results-select-all"
                                    className="checkbox-input ml-6 mr-1"
                                    checked={pageAllSelected}
                                    disabled={results.length === 0}
                                    ref={el =>
                                      el &&
                                      (el.indeterminate = pageSomeSelected)
                                    }
                                    data-cy="Results_selectAllInput"
                                    onChange={e => {
                                      const checked = e.target.checked;

                                      trackEvent(
                                        checked
                                          ? 'SEARCH_SELECT_PAGE'
                                          : 'SEARCH_UNSELECT_PAGE'
                                      );
                                      saveScrollPos();
                                      reportSelectionDispatch({
                                        type: e.target.checked
                                          ? ReportSelectionEventType.SELECT
                                          : ReportSelectionEventType.UNSELECT,
                                        payload: _.map(
                                          results,
                                          result => result['study_id']['raw']
                                        ),
                                      });
                                    }}
                                  />
                                  <div>
                                    <MenuDropdownSingle
                                      buttonText=""
                                      useDefaultButtonClass={false}
                                      buttonClassName="px-2 selectDataDropdownButton"
                                      align="left"
                                      data-cy="selectDataDropdownButton"
                                      menuButtonDisabled={results.length === 0}
                                      menuItems={[
                                        {
                                          itemText: () => (
                                            <div data-cy="selectDataDropdown">
                                              Select all {results.length} on
                                              page
                                            </div>
                                          ),
                                          onClick: () => {
                                            saveScrollPos();
                                            selectPageOfReports();
                                          },
                                        },
                                        {
                                          itemText: () => (
                                            <SelectAllReportsButton />
                                          ),
                                        },
                                        {
                                          itemText: () => (
                                            <button
                                              onClick={e => {
                                                e.preventDefault();
                                                customReportSelectionModalOpenChange(
                                                  true
                                                );
                                              }}
                                            >
                                              Select Custom #
                                            </button>
                                          ),
                                        },
                                      ]}
                                    />
                                  </div>
                                  {fetchStudyIdsMutation.isLoading && (
                                    <div className=" bg-black">
                                      <Loading className="block p-2 text-white" />
                                    </div>
                                  )}
                                  <span
                                    className="ml-2 font-normal"
                                    data-cy="numStudiesSelected"
                                  >{`${reportSelectionState.add.ids.size} Selected`}</span>
                                </div>

                                {/* <AddToDatasetDropdown
                                  disabled={!studiesSelected}
                                  reportSelectionState={reportSelectionState}
                                /> */}
                                <AddToProjectDatasetDropdown
                                  disabled={!studiesSelected}
                                  reportSelectionState={reportSelectionState}
                                  projectId={projectId}
                                />
                                <TagDropdown
                                  tags={userTags}
                                  studyIDs={Array.from(
                                    reportSelectionState.add.ids
                                  )}
                                  align="left"
                                  disabled={!studiesSelected || tagsLoading}
                                />
                                <SaveSearchButton
                                  userId={authState.profile?.userId ?? 0}
                                  studyCount={studyCount}
                                  lastRequestBody={lastRequestBody}
                                  disabled={isSaveSearchDisabled}
                                />
                                {/* The vector search only works with embedded reports on studies green, but this feature should only be available to internal users for now. */}
                                {internalUser && (
                                  <button
                                    className={cx(
                                      'relative inline-flex items-center shadow-sm rounded-md btn py-1',
                                      {
                                        'btn-secondary':
                                          config.searchQuery.useVectorSearch,
                                        'btn-white btn-outline':
                                          !config.searchQuery.useVectorSearch,
                                      }
                                    )}
                                    onClick={toggleVectorSearch}
                                    disabled={AISortingDisabled}
                                  >
                                    <div className="mr-2">
                                      Enable AI Sorting
                                    </div>

                                    <Toggle
                                      checked={
                                        config.searchQuery.useVectorSearch
                                      }
                                      onChange={() => {}}
                                      disabled={
                                        isLoading ||
                                        !lastRequestBody ||
                                        extractSearchTerms(lastRequestBody)
                                          .size === 0 ||
                                        config.searchQuery.groupPatientIDs
                                      }
                                    />
                                  </button>
                                )}
                              </div>
                              {/* ************************************************************ Start of the table here ************************************************************ */}
                              <div className="minerva-search-results border-b border-gray-200 sm:rounded-lg m-2">
                                <div
                                  ref={tableContainerRef}
                                  className="h-[85vh] overflow-auto"
                                  onScroll={saveScrollPos}
                                >
                                  <table className="divide-y divide-gray-200 relative z-0">
                                    <thead className="bg-gray-50 divide-y divide-gray-200 sticky top-0 z-20">
                                      <tr className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                        <th className="sticky top-0 z-20 left-0 w-[39px] bg-white"></th>
                                        <th className="text-sm px-6 py-4 sticky top-0 z-20 bg-white left-[41px] w-[190px]">
                                          Patient ID
                                        </th>
                                        <th className="text-sm px-6 py-4 sticky top-0 z-20 bg-white left-[176px] w-[94px]">
                                          Study ID
                                        </th>
                                        <th className="text-sm px-6 py-4 sticky top-0">
                                          Exam Date
                                        </th>
                                        {isInternalPage ? (
                                          <>
                                            <th className="text-sm px-6 py-4 sticky top-0">
                                              Body Part LLM
                                            </th>
                                            <th className="text-sm px-6 py-4 sticky top-0">
                                              Body Part from Data Partner
                                            </th>
                                            <th className="text-sm px-6 py-4 sticky top-0">
                                              Modality LLM
                                            </th>
                                            <th className="text-sm px-6 py-4 sticky top-0">
                                              Modality from Data Partner
                                            </th>
                                            <th className="text-sm px-6 py-4 sticky top-0">
                                              Contrast
                                            </th>
                                          </>
                                        ) : (
                                          <>
                                            <th className="text-sm px-6 py-4 sticky top-0">
                                              Body Part
                                            </th>
                                            <th className="text-sm px-6 py-4 sticky top-0">
                                              Modality
                                            </th>
                                          </>
                                        )}
                                        <th className="text-sm px-6 py-4 sticky top-0">
                                          View DICOMs
                                        </th>
                                        <th className="text-sm px-6 py-4 sticky top-0">
                                          Title
                                        </th>
                                        <th className="text-sm px-6 py-4 sticky top-0">
                                          Manufacturer
                                        </th>
                                        <th className="text-sm px-6 py-4 sticky top-0">
                                          Patient Age
                                        </th>
                                        <th className="text-sm px-6 py-4 sticky top-0">
                                          Patient Sex
                                        </th>
                                        <th className="text-sm px-6 py-4 sticky top-0">
                                          Model
                                        </th>
                                        <th className="text-sm px-6 py-4 sticky top-0">
                                          Source Location
                                        </th>
                                        <th className="text-sm px-6 py-4 sticky top-0">
                                          Site
                                        </th>
                                        <th className="text-sm px-6 py-4 sticky top-0">
                                          State
                                        </th>
                                        <th className="text-sm px-6 py-4 sticky top-0">
                                          Report (Snippet)
                                        </th>
                                        <th className="text-sm px-6 py-4 sticky top-0">
                                          Dicom Headers (Study)
                                        </th>
                                        <th className="text-sm px-6 py-4 sticky top-0">
                                          Dicom Headers (Series)
                                        </th>
                                        <th className="text-sm px-6 py-4 sticky top-0">
                                          Dicom Headers (Images)
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white ">
                                      {additionalPatientStudies.length &&
                                      showAllStudiesPerPatient ? (
                                        <SearchTableRows
                                          results={intersperseAdditionalStudies(
                                            results,
                                            additionalPatientStudies,
                                            config.searchQuery.sortPatientIDsBy
                                          )}
                                          openedStudyID={openedStudyID}
                                          openedStudyIDChange={
                                            openedStudyIDChange
                                          }
                                          groupPatientIDs={
                                            config.searchQuery.groupPatientIDs
                                          }
                                          dicomsAvailable={dicomAvailability}
                                          saveScrollPos={saveScrollPos}
                                        />
                                      ) : (
                                        <SearchTableRows
                                          results={results}
                                          openedStudyID={openedStudyID}
                                          openedStudyIDChange={
                                            openedStudyIDChange
                                          }
                                          groupPatientIDs={
                                            config.searchQuery.groupPatientIDs
                                          }
                                          dicomsAvailable={dicomAvailability}
                                          internalSearchPage={isInternalPage}
                                          saveScrollPos={saveScrollPos}
                                        />
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {/*  ************************************************************ Start of split view here  ************************************************************  */}
                        {openedStudyID && (
                          <div
                            className={cx({
                              'splitview-panel-r xl:border-l-2 xl:border-b-0 border-b-2 border-gray-400':
                                splitView,
                            })}
                          >
                            <div>
                              <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-4 py-5 sm:px-6 flex justify-between space-x-2">
                                <h3 className="text-base font-semibold leading-6 text-gray-900">
                                  <Link
                                    to={`/study/${openedStudyID}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    preventScrollReset={true}
                                    className="text-primary hover:text-primary-active"
                                  >
                                    {truncateStudyID(openedStudyID)}
                                  </Link>
                                </h3>
                                <button
                                  type="button"
                                  className={cx(
                                    'text-gray-400 hover:text-gray-500'
                                  )}
                                  onClick={() => openedStudyIDChange(undefined)}
                                >
                                  <span className="sr-only">Close</span>

                                  <HiOutlineX className="h-6 w-6" />
                                </button>
                              </div>
                              <div className="p-6">
                                {report && (
                                  <ReportComponent
                                    report={{
                                      ...report,
                                      text:
                                        report.text &&
                                        lastRequestBody &&
                                        extractSearchTerms(lastRequestBody)
                                          ? highlightSearchterm(
                                              extractSearchTerms(
                                                lastRequestBody
                                              ),
                                              report.text
                                            )
                                          : report.text,
                                    }}
                                    PHIReported={() => {}}
                                    splitViewSearch={true}
                                    dicomSeries={getDicomSeries(
                                      openedStudyID,
                                      openedDicomStudy
                                    )}
                                  />
                                )}
                              </div>
                              {!report && (
                                <>
                                  {reportLoading && <Loading />}
                                  {reportError && (
                                    <div>Error loading report</div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Display if nothing has been searched. TODO: remove after IN-1302 */}
                  {!isLoading && wasSearched && (
                    <div className="flex justify-center bottom-0 w-full bg-white p-4 border-t">
                      <Paging />
                    </div>
                  )}
                  {!isLoading && !wasSearched && (
                    <div className="space-y-2 text-gray-900 p-6">
                      <p className="text-gray-600">
                        Use the filters above to search reports and build your
                        dataset.
                      </p>
                    </div>
                  )}
                  <ScrollRestorer
                    tableContainerRef={tableContainerRef}
                    facetContainerRef={facetContainerRef}
                    scrollPosRef={scrollPosRef}
                    dependencies={[
                      results,
                      reportSelectionState,
                      openedStudyID,
                      showAllStudiesPerPatient,
                      additionalPatientStudies,
                    ]}
                  />
                </ErrorBoundary>
              );
            }}
          </WithSearch>
        </div>
      </div>
    </SearchProvider>
  );
};
